"use client"

import { Avatar, Text } from "@/elements"
import { FragmentType, graphql, useFragment } from "@/gql"
import { useSession } from "@/hooks/useSession"
import type { NovaSession } from "@/types"
import clsx from "clsx"
import { useRouter } from "next-nprogress-bar"
import { usePathname } from "next/navigation"
import { NavItemWrapper } from "./NavItemWrapper"

const ProfileNavItemFragment = graphql(`
  fragment ProfileNavItem on User {
    id
    creative {
      handle
      id
      __typename
      ... on IndividualCreative {
        proMembership {
          id
          activeMembershipType
        }
      }
    }
    client {
      handle
      id
    }
  }
`)

interface Props {
  forceHideLabel: boolean
  size: "default" | "lg"
  serverSession: NovaSession | null
  user: FragmentType<typeof ProfileNavItemFragment> | null | undefined
}

export const ProfileNavItem: React.FC<Props> = ({ forceHideLabel, size, serverSession, ...props }) => {
  const user = useFragment(ProfileNavItemFragment, props.user)
  const session = useSession()
  const pathname = usePathname()
  const isClient = session?.data?.account === "client"
  const isLarge = size === "lg"
  const router = useRouter()
  const handle = isClient ? user?.client?.handle : user?.creative?.handle
  const isActive = isClient ? pathname === `/clients/${handle}` : pathname === `/${handle}`
  const creative = user?.creative?.__typename === "IndividualCreative" ? user?.creative : null

  const navigateToProfile = () => {
    if (!handle) return

    if (isClient) {
      router.push(`/clients/${handle}`)
    } else {
      router.push(`/${handle}`)
    }
  }

  const hasSubscription =
    creative?.proMembership?.activeMembershipType && creative.proMembership.activeMembershipType !== "DAY_PASS"

  return (
    <div
      onClick={() => {
        navigateToProfile()
      }}
      className={"cursor-pointer"}>
      <NavItemWrapper>
        <div className="relative size-[24px]">
          <Avatar
            size="xs"
            src={session?.data?.user?.profile?.avatarUrl144w}
            initials={session?.data?.user?.avatarInitials}
          />
        </div>
        <div className="absolute left-[44px] transition-opacity duration-200 ease-in-out md:opacity-0 lg:opacity-100">
          <Text
            variant={isLarge ? "h4" : "body1"}
            className={clsx(
              "select-none md:hidden lg:flex",
              forceHideLabel && "!hidden",
              isActive ? "text-fg-1" : "text-fg-3"
            )}>
            My Profile
          </Text>
        </div>
      </NavItemWrapper>
    </div>
  )
}
